import { useState } from 'react';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import type { DashRegistrationStep } from '@/types/dashboard';
import type { INkrCentersModel, INkrCentersSubmitResult } from '@/types/history/nkr-centers';

const NKR_CENTERS_ENDPOINT = '/api/register/nkr-centers';

const defaultNkrCentersModel: INkrCentersModel = {
    id: 0,
    editKey: '',
    isIE: false,
    isFemale: false,
    isLiver: false,

    distance: 0,
    showAdvanceReimbursementOnly: false,
    dataAsOfDate: null,
    pastYearStartDate: null,

    universalDashOption: null,
    patientCenterOption: null,
    dcnOptions: [],
    nutcrackerOptions: [],
    otherOptions: [],

    languageData: {},
};

export const useNkrCenters = (registrationId: number, editKey: string) => {
    // Null until the donor filters: the server picks the default from the closest
    // DCN center on the first load.
    const [distance, setDistance] = useState<number | null>(null);
    const [showAdvanceReimbursementOnly, setShowAdvanceReimbursementOnly] = useState<boolean | null>(null);

    const url = `${NKR_CENTERS_ENDPOINT}/${registrationId}/${editKey}`;

    // distance is null until the donor filters, which makes it the initial fetch.
    const isInitial = distance == null;
    const loadUrl = isInitial ? url : `${url}?distance=${distance}&showAdvanceReimbursementOnly=${showAdvanceReimbursementOnly}`;

    const { model, isLoading: isFetching, error } = useApiModel(loadUrl, defaultNkrCentersModel);

    const changeFilter = (distance: number, showAdvanceReimbursementOnly: boolean) => {
        setDistance(distance);
        setShowAdvanceReimbursementOnly(showAdvanceReimbursementOnly);
    }

    const { submit, isSubmitting, submitError } = useApiSubmit<number, DashRegistrationStep, INkrCentersSubmitResult>(
        url,
        (centerId) => ({ centerId }),
        (nextStep) => ({ nextStep }),
    );

    return {
        model,
        isLoading: isFetching && isInitial,
        isRefreshing: isFetching && !isInitial,
        error,
        distance,
        changeFilter,
        submit,
        isSubmitting,
        submitError,
    };
};
